import ky, { isHTTPError, isTimeoutError } from "ky";
import { atom } from "nanostores";
import isNetworkError from "is-network-error";
//#region src/api.ts
const DEFAULT_SERVICE_BASE_URLS = {
	dev: "https://forest-api.service.develop-cluster.seekrtech.com",
	staging: "https://forest-api-staging.seekrtech.com",
	global: "https://forest-api.seekrtech.com",
	china: "https://forest-api.upwardsware.com"
};
const DEFAULT_RECEIPT_BASE_URLS = {
	dev: "https://receipt.service.develop-cluster.seekrtech.com",
	staging: "https://receipt.staging.seekrtech.com",
	global: "https://receipt-system.seekrtech.com",
	china: "https://receipt-china.upwardsware.com"
};
/** The Infra API hosts. Each of its services sits under its own path prefix. */
const DEFAULT_INFRA_BASE_URLS = {
	dev: "https://infra-api.service.develop-cluster.seekrtech.com",
	staging: "https://infra-api-staging.seekrtech.com",
	global: "https://infra-api.seekrtech.com",
	china: "https://infra-api.upwardsware.com"
};
const SERVICE_PLACEHOLDER = "https://__paywall_service__.invalid/";
const RECEIPT_PLACEHOLDER = "https://__paywall_receipt__.invalid/";
const INFRA_PLACEHOLDER = "https://__paywall_infra__.invalid/";
function createPaywallApi(options) {
	const { userInfo } = options;
	const serviceServerUrl = options.serviceBaseByServer ?? DEFAULT_SERVICE_BASE_URLS;
	const receiptSystemUrl = options.receiptBaseByServer ?? DEFAULT_RECEIPT_BASE_URLS;
	const infraApiUrl = options.infraBaseByServer ?? DEFAULT_INFRA_BASE_URLS;
	const base = ky.create({
		timeout: 2e4,
		hooks: {
			beforeRequest: [async (request) => {
				const value = await userInfo.getReady();
				if (value.user_type === "legacy") request.headers.set("Remember-Token", value.token);
				else request.headers.set("Authorization", `Bearer ${value.token}`);
				let realBase;
				let path;
				if (request.url.startsWith(SERVICE_PLACEHOLDER)) {
					path = request.url.slice(36);
					realBase = serviceServerUrl[value.server];
				} else if (request.url.startsWith(RECEIPT_PLACEHOLDER)) {
					path = request.url.slice(36);
					realBase = receiptSystemUrl[value.server];
				} else if (request.url.startsWith(INFRA_PLACEHOLDER)) {
					path = request.url.slice(34);
					realBase = infraApiUrl[value.server];
				}
				if (realBase != null && path !== void 0) {
					const body = request.method === "GET" || request.method === "HEAD" ? void 0 : await request.clone().arrayBuffer();
					return new Request(`${realBase.replace(/\/$/, "")}/${path}`, {
						method: request.method,
						headers: request.headers,
						body,
						signal: request.signal,
						credentials: request.credentials,
						cache: request.cache,
						mode: request.mode,
						redirect: request.redirect,
						referrer: request.referrer,
						referrerPolicy: request.referrerPolicy,
						integrity: request.integrity,
						keepalive: request.keepalive
					});
				}
			}],
			afterResponse: [async (request, _, response) => {
				if (response.status === 401 || response.status === 403) {
					const value = userInfo.get();
					if (!value) throw new Error("User info not set");
					if (value.user_type === "legacy") return response;
					const refreshed = await userInfo.refresh();
					request.headers.set("Authorization", `Bearer ${refreshed.token}`);
					return await ky(request);
				}
				return response;
			}]
		}
	});
	return {
		service: base.extend({ prefixUrl: SERVICE_PLACEHOLDER }),
		receipt: base.extend({ prefixUrl: RECEIPT_PLACEHOLDER }),
		infra: base.extend({ prefixUrl: INFRA_PLACEHOLDER })
	};
}
//#endregion
//#region src/stores.ts
function createProductsStore(initial = null) {
	return atom(initial);
}
function createBooleanStore(initial) {
	return atom(initial);
}
//#endregion
//#region src/user-info-source.ts
function createUserInfoSource(options) {
	const store = atom(null);
	let loadPromise = null;
	function getReady() {
		const current = store.get();
		if (current) return Promise.resolve(current);
		if (loadPromise) return loadPromise;
		const inFlight = options.load().then((value) => {
			store.set(value);
			return value;
		}, (error) => {
			if (loadPromise === inFlight) loadPromise = null;
			throw error;
		});
		loadPromise = inFlight;
		return loadPromise;
	}
	async function refresh() {
		const value = await options.refresh();
		store.set(value);
		return value;
	}
	return {
		atom: store,
		get: () => store.get(),
		getReady,
		refresh
	};
}
//#endregion
//#region src/errors.ts
var NativeStoreError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "NativeStoreError";
	}
};
/**
* The promotional offer signature could not be fetched, so the purchase was
* stopped before its order (ADR 0036). `cause` is the request's error.
*/
var PromotionalOfferSignatureError = class extends Error {
	constructor(cause) {
		super("Promotional offer signature request failed", { cause });
		this.name = "PromotionalOfferSignatureError";
	}
};
/**
* Payment was taken, but a later step failed, so the subscription is not
* linked. `cause` is that step's error.
*/
var AfterPaymentError = class extends Error {
	purchaseId;
	transactionId;
	node;
	constructor(purchaseId, transactionId, node, cause) {
		super("Purchase failed", { cause });
		this.name = "AfterPaymentError";
		this.purchaseId = purchaseId;
		this.transactionId = transactionId;
		this.node = node;
	}
};
/**
* The store handed back a stale transaction again after every unclosed
* subscription transaction was closed, so no payment was taken and the
* purchase was cancelled (ADR 0045). `cause` is the second upload's 410.
*/
var StaleTransactionError = class extends Error {
	purchaseId;
	transactionId;
	constructor(purchaseId, transactionId, cause) {
		super("Store handed back a stale transaction", { cause });
		this.name = "StaleTransactionError";
		this.purchaseId = purchaseId;
		this.transactionId = transactionId;
	}
};
/**
* The Receipt System answered the upload with 409 or 412: another Forest
* account owns the subscription (a subscription conflict, ADR 0045).
* `ownerUid` is that account's uid from the answer's `owner_uid`, or `null`
* when the answer names none. `cause` is the HTTP error.
*/
var SubscriptionConflictError = class extends Error {
	ownerUid;
	constructor(ownerUid, cause) {
		super(`Subscription owned by another Forest account (owner_uid: ${ownerUid ?? "none"})`, { cause });
		this.name = "SubscriptionConflictError";
		this.ownerUid = ownerUid;
	}
};
/**
* A payment may have succeeded, but the page cannot yet prove it: a China
* Android interruption that recovery has not confirmed (GLOSSARY.md, Purchase
* confirmation dialog). The purchase stays open, so a late confirmation can
* still complete it. `failure` is what the dialog shows (ADR 0037). `cause` is
* the last failed request, if any.
*/
var PurchaseConfirmationPendingError = class extends Error {
	failure;
	constructor(failure, cause) {
		super("Purchase confirmation pending", { cause });
		this.name = "PurchaseConfirmationPendingError";
		this.failure = failure;
	}
};
//#endregion
//#region src/failure.ts
function failureCode(step, cause) {
	return `${step}:${cause}`;
}
const FAILURE_TAG = Symbol.for("@forest/paywall-core/failure");
/**
* Records on `error` the step it failed at, so whoever shows its dialog can
* name it. The cause is read now, before anything (step telemetry renames the
* error) can change what it looks like. The first tag wins: it is the
* innermost step. Returns `error`, for `throw tagFailure(error, step)`.
*/
function tagFailure(error, step, details = {}) {
	if (typeof error === "object" && error !== null && !(FAILURE_TAG in error)) {
		const tag = {
			step,
			cause: details.cause ?? failureCause(error),
			...details.purchaseId !== void 0 && { purchaseId: details.purchaseId },
			...details.transactionId !== void 0 && { transactionId: details.transactionId }
		};
		Object.defineProperty(error, FAILURE_TAG, { value: tag });
	}
	return error;
}
/** The tag `tagFailure` recorded, or the fallback step with the error's cause. */
function readFailureTag(error, fallbackStep) {
	if (typeof error === "object" && error !== null && FAILURE_TAG in error) return error[FAILURE_TAG];
	return {
		step: fallbackStep,
		cause: failureCause(error)
	};
}
/**
* The failure a dialog shows for `error`: its tagged step, or `fallbackStep`
* when nothing tagged it, with the IDs the tag recorded.
*/
function failureOf(error, fallbackStep) {
	const { step, cause, purchaseId, transactionId } = readFailureTag(error, fallbackStep);
	return {
		code: failureCode(step, cause),
		...purchaseId !== void 0 && { purchaseId },
		...transactionId !== void 0 && { transactionId }
	};
}
/**
* Whether `error` never reached the server or did not answer in time — what a
* connection issue dialog is for. Decided by the failure's cause, so the
* dialog never disagrees with the code it shows.
*/
function isConnectionFailure(error) {
	const { cause } = readFailureTag(error, "load");
	return cause === "network" || cause === "timeout";
}
/**
* `description` with the failure's lines under it: the code, followed by
* ` #<purchase ID>`, then the transaction ID on a line of its own. A
* description that already shows the code (`codeInDescription`) gains only the
* IDs.
*/
function withFailure(description, failure, options = {}) {
	return [
		description,
		[options.codeInDescription ? void 0 : failure.code, failure.purchaseId === void 0 ? void 0 : `#${failure.purchaseId}`].filter(Boolean).join(" "),
		failure.transactionId
	].filter(Boolean).join("\n");
}
/**
* An error whose cause is known where it is thrown — data a step found
* missing (`sku_missing`), or a native call's error — so the failure code can
* name it instead of `unexpected`.
*/
var FailureCauseError = class extends Error {
	failureCause;
	constructor(failureCause, message) {
		super(message);
		this.name = "FailureCauseError";
		this.failureCause = failureCause;
	}
};
/** The cause for a native call's `{ success: false, error }`. */
function nativeErrorCause(error) {
	return snakeCase(error);
}
/**
* The cause half of a failure code (ADR 0037): what went wrong, never a raw
* error message.
*/
function failureCause(error) {
	if (error instanceof FailureCauseError) return error.failureCause;
	if (error instanceof NativeStoreError) return snakeCase(error.message);
	if (error instanceof PromotionalOfferSignatureError || error instanceof AfterPaymentError || error instanceof SubscriptionConflictError || error instanceof StaleTransactionError) return failureCause(error.cause);
	if (isHTTPError(error)) return `http_${error.response.status}`;
	if (isTimeoutError(error)) return "timeout";
	if (error instanceof Error && (isNetworkError(error) || isWebKitLoadFailed(error))) return "network";
	return bridgeCause(error) ?? "unexpected";
}
/**
* WebKit's failed `fetch()`, optionally suffixed with a hostname.
* `is-network-error` matches it only without a stack, but in the iOS WKWebView
* it often carries one.
*/
function isWebKitLoadFailed(error) {
	return error instanceof TypeError && (error.message === "Load failed" || error.message.startsWith("Load failed (") && error.message.endsWith(")"));
}
/**
* bifrost-web's call errors, matched by name: paywall-core does not depend on
* bifrost-web, and native-services' wrappers reject with them as they are.
*/
const BRIDGE_ERROR_NAMES = /* @__PURE__ */ new Set([
	"DeliveryUnknownError",
	"TransportNotAdmittedError",
	"OverloadedError",
	"CallDeadlineExceededError",
	"CallCancelledError",
	"PayloadTooLargeError",
	"SessionReplacedError"
]);
const BRIDGE_TIMEOUT_MESSAGES = /* @__PURE__ */ new Set(["RPC call timeout", "Connection timeout"]);
function bridgeCause(error) {
	if (!(error instanceof Error)) return void 0;
	if (error.name === "RpcCallError" && "code" in error) return `bridge_${String(error.code)}`;
	if (BRIDGE_ERROR_NAMES.has(error.name)) return `bridge_${snakeCase(error.name.replace(/Error$/, ""))}`;
	if (BRIDGE_TIMEOUT_MESSAGES.has(error.message)) return "bridge_timeout";
}
/** `product not found` → `product_not_found`, `CallCancelled` → `call_cancelled`. */
function snakeCase(value) {
	return value.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}
//#endregion
//#region src/subscribe.ts
const PaymentSystemID = {
	PingXX: 7,
	GooglePlay: 8,
	AppStoreStoreKit1: 9,
	AppStoreStoreKit2: 11
};
const paymentSystemIdByPlatform = {
	china_android: PaymentSystemID.PingXX,
	googleplay_android: PaymentSystemID.GooglePlay,
	ios: PaymentSystemID.AppStoreStoreKit2
};
async function subscribe({ api, platform, userId, product, nativePurchase, closeTransaction, getAllUnclosedTransactions, receiptSystemAccountId, offer, shouldResolve, fetchServiceLevelStatus, readServiceLevel, debugSimulateAfterPaymentError, onStepError, logEvent }) {
	const logRequestErrorDialog = (error) => {
		const code = classifyErrorCode(error);
		logEvent?.({
			event_name: "component_event",
			event_params: {
				type: "dialog",
				app_action: "show",
				component_name: code === "network" || code === "timeout" ? "internet_issue" : `error_${code}`
			}
		});
	};
	/**
	* Signs an iOS promotional offer; `undefined` when the purchase uses none.
	* Its nonce is single-use, so every attempt to buy fetches a fresh
	* signature (ADR 0036). Throws a tagged `PromotionalOfferSignatureError`.
	*/
	const signPromotionalOffer = async () => {
		if (platform !== "ios" || product.product_type !== "subscription" || offer?.type !== "promotional") return;
		try {
			return await fetchPromotionalOfferSignature(api.infra, {
				userId,
				sku: product.sku,
				offerId: offer.id
			});
		} catch (cause) {
			logRequestErrorDialog(cause);
			const error = tagFailure(new PromotionalOfferSignatureError(cause), "sign_promotional_offer");
			onStepError?.(error, { step: "sign_promotional_offer" });
			throw error;
		}
	};
	/**
	* The subscription state before a China Android subscription purchase (the
	* baseline interrupted recovery compares against); `undefined` on every
	* other purchase, which reads none. Throws, tagged, when it cannot be read:
	* the purchase must not start without it.
	*/
	const readBaseline = async () => {
		if (platform !== "china_android" || product.product_type !== "subscription") return;
		try {
			return await readServiceLevelStatus({
				readServiceLevel,
				serviceApi: api.service,
				userId
			});
		} catch (error) {
			logRequestErrorDialog(error);
			tagFailure(error, "read_service_level");
			onStepError?.(error, { step: "read_service_level" });
			throw error;
		}
	};
	const baseline = await readBaseline();
	let promotionalOfferSignature = await signPromotionalOffer();
	let purchaseId;
	try {
		purchaseId = (await api.service.post(`store/users/${userId}/purchase-right`, { json: {
			product_id: product.product_id,
			iap_sku: product.sku
		} }).json()).results[0].purchase.id;
	} catch (error) {
		logRequestErrorDialog(error);
		tagFailure(error, "create_order");
		onStepError?.(error, { step: "create_order" });
		throw error;
	}
	/**
	* Marks the purchase record cancelled (no payment was taken) or failed,
	* without waiting: the purchase's outcome does not depend on it.
	*/
	const markPurchase = (outcome) => {
		api.service.post(`store/users/${userId}/purchase/${purchaseId}/mark-${outcome}`).catch(() => {});
	};
	const postTransaction = async (transactionId) => {
		let receiptRequestUrl = `projects/6/users/${userId}`;
		let data = {};
		if (platform === "china_android") {
			receiptRequestUrl += "/pending_iap_receipts";
			data = { receipt_data: transactionId };
		} else {
			receiptRequestUrl += "/subscriptions";
			data = { transaction_id: transactionId };
		}
		await api.receipt.post(receiptRequestUrl, { json: {
			...data,
			sku_id: product.sku,
			payment_system_id: paymentSystemIdByPlatform[platform]
		} });
	};
	const postTransactionFailed = (error, transactionId) => {
		const afterPaymentError = tagFailure(new AfterPaymentError(purchaseId, transactionId, "post_transaction", error), "post_transaction", {
			purchaseId,
			transactionId
		});
		onStepError?.(error, {
			step: "post_transaction",
			purchaseId,
			transactionId
		});
		return afterPaymentError;
	};
	/**
	* The store still handed back a stale transaction after close all: no
	* payment was taken, so marks the purchase cancelled, not left awaiting
	* Restore Purchases (ADR 0045), and returns the error to throw. No third
	* purchase is made.
	*/
	const cancelStalePurchase = (error, transactionId) => {
		markPurchase("cancelled");
		const staleTransactionError = tagFailure(new StaleTransactionError(purchaseId, transactionId, error), "post_transaction", { purchaseId });
		onStepError?.(error, {
			step: "post_transaction",
			purchaseId,
			transactionId
		});
		return staleTransactionError;
	};
	const logPaidFailDialog = (storeError) => {
		logEvent?.({
			event_name: "component_event",
			event_params: {
				type: "dialog",
				app_action: "show",
				component_name: `sub_paid_fail_${storeError.replace(/ /g, "_")}`
			}
		});
	};
	/**
	* Waits for the service level to show a recovered China Android purchase
	* (GLOSSARY.md) against `baseline`, and returns that status. The purchase is
	* never marked cancelled or failed, so a late Ping++ confirmation can still
	* complete it. At the deadline, throws `PurchaseConfirmationPendingError`
	* (ADR 0052).
	*/
	const confirmInterruptedPurchase = async (baseline) => {
		let lastError;
		try {
			return await waitForServiceLevelFromSource({
				fetchStatus: async () => {
					try {
						return {
							success: true,
							data: await readServiceLevelStatus({
								readServiceLevel,
								serviceApi: api.service,
								userId
							})
						};
					} catch (error) {
						lastError = error;
						throw error;
					}
				},
				shouldResolve: (status) => isRecoveredSubscriptionStatus(status, baseline)
			});
		} catch {
			const cause = lastError === void 0 ? "timeout" : failureCause(lastError);
			if (lastError !== void 0) onStepError?.(lastError, {
				step: "recover_interrupted_purchase",
				purchaseId
			});
			logPaidFailDialog("interrupted");
			throw new PurchaseConfirmationPendingError({
				code: failureCode("recover_interrupted_purchase", cause),
				purchaseId
			}, lastError);
		}
	};
	const executeNativePurchase = async () => {
		const payload = product.product_type === "subscription" ? {
			product_type: "subscription",
			product_id: product.product_id,
			sku: product.sku,
			offer_id: offer?.id,
			...promotionalOfferSignature !== void 0 && { offer_compact_jws: promotionalOfferSignature },
			receipt_system_account_id: receiptSystemAccountId
		} : {
			product_type: "consumable",
			product_id: product.product_id,
			sku: product.sku,
			receipt_system_account_id: receiptSystemAccountId
		};
		let result;
		try {
			result = await nativePurchase(payload);
		} catch (error) {
			tagFailure(error, "native_purchase", { purchaseId });
			onStepError?.(error, {
				step: "native_purchase",
				purchaseId
			});
			throw error;
		}
		if (!result.success) {
			if (baseline !== void 0 && result.error === "interrupted") return confirmInterruptedPurchase(baseline);
			markPurchase(result.error === "interrupted" ? "cancelled" : "failed");
			const error = new NativeStoreError(result.error);
			tagFailure(error, "native_purchase", { purchaseId });
			logPaidFailDialog(error.message);
			onStepError?.(error, {
				step: "native_purchase",
				purchaseId
			});
			throw error;
		}
		return result.data.transaction_id;
	};
	/**
	* Closes every unclosed subscription transaction ("close all", ADR 0045):
	* the rejected one, and every subscription one the store lists, leaving
	* consumables alone (the same filter restore uses). Never throws: a failed
	* listing or close is reported, and the caller goes on.
	*/
	const closeAllUnclosedSubscriptionTransactions = async (rejectedTransactionId) => {
		const transactionIds = /* @__PURE__ */ new Set([rejectedTransactionId]);
		try {
			const listing = await withTimeout(getAllUnclosedTransactions, "Listing unclosed transactions timed out");
			if (!listing.success) throw new FailureCauseError("native_failed", "Listing unclosed transactions failed");
			for (const transaction of listing.data.transactions) if (transaction.product_type === "subscription") transactionIds.add(transaction.transaction_id);
		} catch (error) {
			onStepError?.(error, {
				step: "list_unclosed_transactions",
				purchaseId
			});
		}
		for (const transaction_id of transactionIds) try {
			await withTimeout(() => closeTransaction({ transaction_id }), "Closing an unclosed transaction timed out");
		} catch (error) {
			onStepError?.(error, {
				step: "close_transaction",
				purchaseId,
				transactionId: transaction_id
			});
		}
	};
	/**
	* Ends a purchase on a subscription conflict (409/412): closes every unclosed
	* subscription transaction and returns the error to throw, naming the
	* owning account. The purchase record is left as it is.
	*/
	const closeAllOnSubscriptionConflict = async (error, rejectedTransactionId) => {
		const ownerUid = await readOwnerUid(error);
		await closeAllUnclosedSubscriptionTransactions(rejectedTransactionId);
		const conflictError = tagFailure(new SubscriptionConflictError(ownerUid, error), "post_transaction", {
			purchaseId,
			transactionId: rejectedTransactionId
		});
		onStepError?.(error, {
			step: "post_transaction",
			purchaseId,
			transactionId: rejectedTransactionId
		});
		return conflictError;
	};
	const initialPurchase = await executeNativePurchase();
	if (typeof initialPurchase !== "string") return initialPurchase;
	let transactionId = initialPurchase;
	if (debugSimulateAfterPaymentError) throw new AfterPaymentError(purchaseId, transactionId, "debug_simulation");
	try {
		await api.service.post(`store/users/${userId}/purchase/${purchaseId}/mark-processing`);
	} catch (error) {
		const afterPaymentError = tagFailure(new AfterPaymentError(purchaseId, transactionId, "mark_processing", error), "mark_processing", {
			purchaseId,
			transactionId
		});
		onStepError?.(error, {
			step: "mark_processing",
			purchaseId,
			transactionId
		});
		throw afterPaymentError;
	}
	try {
		await postTransaction(transactionId);
	} catch (error) {
		const rejection = classifyUploadRejection(error);
		if (rejection === null) throw postTransactionFailed(error, transactionId);
		if (rejection === "subscription_conflict") throw await closeAllOnSubscriptionConflict(error, transactionId);
		await closeAllUnclosedSubscriptionTransactions(transactionId);
		try {
			promotionalOfferSignature = await signPromotionalOffer();
		} catch (signatureError) {
			markPurchase("cancelled");
			throw signatureError;
		}
		const retryPurchase = await executeNativePurchase();
		if (typeof retryPurchase !== "string") return retryPurchase;
		transactionId = retryPurchase;
		try {
			await postTransaction(transactionId);
		} catch (retryError) {
			switch (classifyUploadRejection(retryError)) {
				case "stale_transaction": throw cancelStalePurchase(retryError, transactionId);
				case "subscription_conflict": throw await closeAllOnSubscriptionConflict(retryError, transactionId);
				default: throw postTransactionFailed(retryError, transactionId);
			}
		}
	}
	try {
		await closeTransaction({ transaction_id: transactionId });
	} catch (error) {
		onStepError?.(error, {
			step: "close_transaction",
			purchaseId,
			transactionId
		});
	}
	try {
		return fetchServiceLevelStatus ? await waitForServiceLevelFromSource({
			fetchStatus: fetchServiceLevelStatus,
			shouldResolve
		}) : await waitForSubscriptionStatus({
			serviceApi: api.service,
			userId,
			shouldResolve
		});
	} catch (error) {
		onStepError?.(error, {
			step: "poll_service_level",
			purchaseId,
			transactionId
		});
		console.error("Failed to poll service level status", error);
		return null;
	}
}
/** A promotional offer signature from the Infra API (ADR 0036). */
async function fetchPromotionalOfferSignature(infraApi, { userId, sku, offerId }) {
	return (await infraApi.post(`receipt/projects/6/users/${userId}/promotional_offers/signatures`, { json: {
		sku_id: sku,
		offer_identifier: offerId
	} }).json()).compact_jws;
}
/**
* Runs a native call, rejecting with a `timeout` cause when it has not
* settled within `timeoutMs` (5 s by default): the native bridge's own timeout
* is far longer, and a purchase must not wait on it. A synchronous throw
* rejects too.
*/
async function withTimeout(run, message, timeoutMs = NATIVE_TRANSACTION_CALL_TIMEOUT_MS) {
	let timer;
	try {
		return await Promise.race([Promise.resolve().then(run), new Promise((_, reject) => {
			timer = setTimeout(() => reject(new FailureCauseError("timeout", message)), timeoutMs);
		})]);
	} finally {
		clearTimeout(timer);
	}
}
function classifyErrorCode(error) {
	if (isHTTPError(error)) return String(error.response.status);
	if (isTimeoutError(error)) return "timeout";
	if (isNetworkError(error)) return "network";
	return "unknown";
}
/**
* What the Receipt System means by rejecting an upload (ADR 0045): 410, the
* transaction has expired (a stale transaction); 409/412, another Forest
* account owns the subscription (a subscription conflict). `null` for any
* other failure.
*/
function classifyUploadRejection(error) {
	if (!isHTTPError(error)) return null;
	switch (error.response.status) {
		case 410: return "stale_transaction";
		case 409:
		case 412: return "subscription_conflict";
		default: return null;
	}
}
/**
* The owning account's uid from a 409/412 answer's `{ owner_uid }` (a
* subscription conflict, ADR 0045), or `null` when the answer names none (a
* 412 may not), cannot be read, or `error` is not an HTTP error.
*/
async function readOwnerUid(error) {
	if (!isHTTPError(error)) return null;
	try {
		const body = await error.response.clone().json();
		const ownerUid = typeof body === "object" && body !== null && "owner_uid" in body ? body.owner_uid : null;
		return typeof ownerUid === "number" ? ownerUid : null;
	} catch {
		return null;
	}
}
const SERVICE_LEVEL_POLL_INTERVAL_MS = 1e3;
const SERVICE_LEVEL_POLL_TIMEOUT_MS = 15e3;
const NATIVE_TRANSACTION_CALL_TIMEOUT_MS = 5e3;
const NATIVE_SERVICE_LEVEL_READ_TIMEOUT_MS = 3e3;
const SERVICE_LEVEL_READ_TIMEOUT_MS = 1e4;
/**
* Polls the Service API's service-level status until `shouldResolve` accepts
* it: one request at a time, each starting `intervalMs` (default 1 s) after
* the previous one returns, until `timeoutMs` (default 15 s) passes. A failed
* request ends the poll with its error.
*/
async function waitForSubscriptionStatus({ serviceApi, userId, intervalMs = SERVICE_LEVEL_POLL_INTERVAL_MS, timeoutMs = SERVICE_LEVEL_POLL_TIMEOUT_MS, signal, shouldResolve = isSubscriptionStatus }) {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
	const combinedSignal = mergeAbortSignals(signal, controller.signal);
	try {
		return await pollServiceLevelStatus(serviceApi, userId, combinedSignal, intervalMs, shouldResolve);
	} catch (error) {
		if (combinedSignal?.aborted) throw new Error("Polling service level timed out");
		throw error;
	} finally {
		clearTimeout(timeoutId);
	}
}
async function pollServiceLevelStatus(serviceApi, userId, signal, intervalMs, shouldResolve) {
	while (true) {
		if (signal?.aborted) throw new Error("Polling service level aborted");
		const status = await getServiceLevelStatus(serviceApi, userId, { signal });
		if (shouldResolve(status)) return status;
		await sleep(intervalMs, signal);
	}
}
/**
* Polls an injected source sequentially: each attempt starts 1 s after the
* previous one returns. Failed attempts are skipped, and the deadline bounds
* the whole poll — including an attempt that never returns.
*/
async function waitForServiceLevelFromSource({ fetchStatus, shouldResolve = isSubscriptionStatus }) {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), SERVICE_LEVEL_POLL_TIMEOUT_MS);
	const deadline = new Promise((_, reject) => {
		controller.signal.addEventListener("abort", () => reject(/* @__PURE__ */ new Error("Polling service level timed out")), { once: true });
	});
	deadline.catch(() => {});
	const poll = async () => {
		while (!controller.signal.aborted) {
			try {
				const result = await fetchStatus();
				if (result.success && shouldResolve(result.data)) return result.data;
			} catch {}
			await sleep(SERVICE_LEVEL_POLL_INTERVAL_MS, controller.signal);
		}
		throw new Error("Polling service level timed out");
	};
	try {
		return await Promise.race([poll(), deadline]);
	} finally {
		clearTimeout(timeoutId);
		controller.abort();
	}
}
/**
* Reads the user's service level: through `readServiceLevel` first, then with
* one Service API request when it returns no status (`unsupported` on an old
* app, a native error, a rejection, a synchronous throw, or no answer within
* 3 s). Throws the Service API request's error when both fail.
*/
async function readServiceLevelStatus({ readServiceLevel, serviceApi, userId }) {
	try {
		const result = await withTimeout(readServiceLevel, "Reading the service level timed out", NATIVE_SERVICE_LEVEL_READ_TIMEOUT_MS);
		if (result?.success) return result.data;
	} catch {}
	return getServiceLevelStatus(serviceApi, userId, {
		retry: 0,
		timeout: SERVICE_LEVEL_READ_TIMEOUT_MS
	});
}
/** One Service API read of the user's service level. */
function getServiceLevelStatus(serviceApi, userId, options) {
	return serviceApi.get(`user/users/${userId}/service-levels/status`, options).json();
}
function isSubscriptionStatus(status) {
	return status.service_level === "subscription";
}
/**
* Whether `status` shows a recovered China Android purchase (GLOSSARY.md)
* against `baseline`: the user became subscribed, or the subscription's expiry
* is strictly later than the baseline's.
*/
function isRecoveredSubscriptionStatus(status, baseline) {
	if (!isSubscriptionStatus(status)) return false;
	if (!isSubscriptionStatus(baseline)) return true;
	const recoveredExpiry = Date.parse(status.subscription.expires_at);
	const baselineExpiry = Date.parse(baseline.subscription.expires_at);
	return Number.isFinite(recoveredExpiry) && Number.isFinite(baselineExpiry) && recoveredExpiry > baselineExpiry;
}
function sleep(ms, signal) {
	return new Promise((resolve, reject) => {
		const timer = setTimeout(resolve, ms);
		if (signal) signal.addEventListener("abort", () => {
			clearTimeout(timer);
			reject(/* @__PURE__ */ new Error("Polling service level aborted"));
		}, { once: true });
	});
}
function mergeAbortSignals(...signals) {
	const activeSignals = signals.filter(Boolean);
	if (activeSignals.length === 0) return void 0;
	const controller = new AbortController();
	const onAbort = () => controller.abort();
	activeSignals.forEach((sig) => sig.addEventListener("abort", onAbort));
	if (activeSignals.some((sig) => sig.aborted)) controller.abort();
	return controller.signal;
}
//#endregion
//#region src/types.ts
function mapPlatformToServiceApi(platform) {
	switch (platform) {
		case "ios": return "ios";
		case "googleplay_android": return "android";
		case "china_android": return "android-cn";
	}
}
//#endregion
//#region src/packs.ts
const PRESETS = {
	staging: {
		ios: {
			annual_default: {
				sku: "forest_annual_1",
				receipt_system_product_id: 2,
				product_id: 21
			},
			annual_early_bird: {
				sku: "forest_annual_early_bird",
				receipt_system_product_id: 6,
				product_id: 22
			},
			monthly_default: {
				sku: "forest_monthly_1",
				receipt_system_product_id: 1,
				product_id: 37
			},
			monthly_early_bird: {
				sku: "forest_monthly_early_bird",
				receipt_system_product_id: 5,
				product_id: 38
			}
		},
		googleplay_android: {
			annual_default: {
				sku: "forest_plus-annual-1",
				receipt_system_product_id: 4,
				product_id: 23
			},
			annual_early_bird: {
				sku: "forest_plus-annual-early-bird",
				receipt_system_product_id: 8,
				product_id: 24
			},
			monthly_default: {
				sku: "forest_plus-monthly-1",
				receipt_system_product_id: 3,
				product_id: 39
			},
			monthly_early_bird: {
				sku: "forest_plus-monthly-early-bird",
				receipt_system_product_id: 7,
				product_id: 40
			}
		},
		china_android: {
			annual_default: {
				sku: "plus_card_annual_1",
				receipt_system_product_id: 23,
				product_id: 46
			},
			annual_early_bird: {
				sku: "plus_card_annual_early_bird_1",
				receipt_system_product_id: 24,
				product_id: 47
			},
			monthly_default: {
				sku: "plus_card_monthly_1",
				receipt_system_product_id: 21,
				product_id: 44
			},
			monthly_early_bird: {
				sku: "plus_card_monthly_early_bird_1",
				receipt_system_product_id: 22,
				product_id: 45
			}
		}
	},
	global: {
		ios: {
			annual_default: {
				sku: "forest_annual_1",
				receipt_system_product_id: 2,
				product_id: 15
			},
			annual_early_bird: {
				sku: "forest_annual_early_bird",
				receipt_system_product_id: 6,
				product_id: 16
			},
			monthly_default: {
				sku: "forest_monthly_1",
				receipt_system_product_id: 1,
				product_id: 27
			},
			monthly_early_bird: {
				sku: "forest_monthly_early_bird",
				receipt_system_product_id: 5,
				product_id: 28
			}
		},
		googleplay_android: {
			annual_default: {
				sku: "forest_plus-annual-1",
				receipt_system_product_id: 4,
				product_id: 17
			},
			annual_early_bird: {
				sku: "forest_plus-annual-early-bird",
				receipt_system_product_id: 8,
				product_id: 18
			},
			monthly_default: {
				sku: "forest_plus-monthly-1",
				receipt_system_product_id: 3,
				product_id: 29
			},
			monthly_early_bird: {
				sku: "forest_plus-monthly-early-bird",
				receipt_system_product_id: 7,
				product_id: 30
			}
		},
		china_android: {
			annual_default: {
				sku: "plus_card_annual_1",
				receipt_system_product_id: 23,
				product_id: 36
			},
			annual_early_bird: {
				sku: "plus_card_annual_early_bird_1",
				receipt_system_product_id: 24,
				product_id: 37
			},
			monthly_default: {
				sku: "plus_card_monthly_1",
				receipt_system_product_id: 21,
				product_id: 34
			},
			monthly_early_bird: {
				sku: "plus_card_monthly_early_bird_1",
				receipt_system_product_id: 22,
				product_id: 35
			}
		}
	},
	china: {
		ios: {
			annual_default: {
				sku: "forest_annual_1",
				receipt_system_product_id: 2,
				product_id: 15
			},
			annual_early_bird: {
				sku: "forest_annual_early_bird",
				receipt_system_product_id: 6,
				product_id: 16
			},
			monthly_default: {
				sku: "forest_monthly_1",
				receipt_system_product_id: 1,
				product_id: 27
			},
			monthly_early_bird: {
				sku: "forest_monthly_early_bird",
				receipt_system_product_id: 5,
				product_id: 28
			}
		},
		googleplay_android: {
			annual_default: {
				sku: "forest_plus-annual-1",
				receipt_system_product_id: 4,
				product_id: 17
			},
			annual_early_bird: {
				sku: "forest_plus-annual-early-bird",
				receipt_system_product_id: 8,
				product_id: 18
			},
			monthly_default: {
				sku: "forest_plus-monthly-1",
				receipt_system_product_id: 3,
				product_id: 29
			},
			monthly_early_bird: {
				sku: "forest_plus-monthly-early-bird",
				receipt_system_product_id: 7,
				product_id: 30
			}
		},
		china_android: {
			annual_default: {
				sku: "plus_card_annual_1",
				receipt_system_product_id: 23,
				product_id: 36
			},
			annual_early_bird: {
				sku: "plus_card_annual_early_bird_1",
				receipt_system_product_id: 24,
				product_id: 37
			},
			monthly_default: {
				sku: "plus_card_monthly_1",
				receipt_system_product_id: 21,
				product_id: 34
			},
			monthly_early_bird: {
				sku: "plus_card_monthly_early_bird_1",
				receipt_system_product_id: 22,
				product_id: 35
			}
		}
	}
};
function resolvePreset(server) {
	if (server === "dev") return PRESETS.staging;
	return PRESETS[server];
}
function createSubscriptionPacks(options) {
	const { server, platform } = options;
	const platformValues = resolvePreset(server)[platform];
	if (!platformValues) throw new Error(`Missing pack config for platform: ${platform}`);
	return {
		annualEarlyBird: toMetadata(platformValues.annual_early_bird),
		annualDefault: toMetadata(platformValues.annual_default),
		monthlyEarlyBird: toMetadata(platformValues.monthly_early_bird),
		monthlyDefault: toMetadata(platformValues.monthly_default)
	};
}
function toMetadata(values) {
	return {
		product_type: "subscription",
		sku: values.sku,
		receipt_system_product_id: values.receipt_system_product_id,
		product_id: values.product_id
	};
}
//#endregion
//#region src/auth.ts
var AbortError = class extends Error {
	constructor(message = "aborted") {
		super(message);
		this.name = "AbortError";
	}
};
async function ensureAuthenticatedUser(options) {
	let userInfo = await options.getUserInfo().then((result) => {
		if (!result.success) throw new Error("getUserInfo failed");
		if (!result.data) throw new Error("getUserInfo returned null data");
		return result.data;
	});
	const wasGuest = userInfo.user_type === "guest";
	const guestUserId = userInfo.user_id;
	if (userInfo.user_type === "guest") {
		await options.onBeforeGuestUpgrade?.();
		const guestAuthResult = await options.auth();
		if (!guestAuthResult.success) throw new AbortError();
		if (!guestAuthResult.data) throw new Error("auth returned null data");
		userInfo = guestAuthResult.data;
	}
	if (userInfo.user_type === "legacy") {
		await options.onBeforeLegacyBind?.();
		if (!(await options.bindLegacyUser()).success) throw new AbortError();
		await options.onLegacyBindStart?.();
		while (true) {
			const legacyAuthResult = await options.auth();
			if (!legacyAuthResult.success) continue;
			if (!legacyAuthResult.data) continue;
			userInfo = legacyAuthResult.data;
			break;
		}
		await options.onLegacyBindEnd?.();
	}
	if (userInfo.user_type !== "seekr_id") throw new Error("Somehow not authenticated");
	if (wasGuest && userInfo.user_id !== guestUserId) await options.onAfterGuestUpgrade?.();
	await options.onUserInfo?.(userInfo);
	return userInfo;
}
//#endregion
//#region src/trial.ts
const TRIAL_PRODUCT_IDS = {
	dev: 43,
	staging: 43,
	production: 33
};
/**
* Get China Android free trial product id by build mode.
* Returns undefined when not configured for the provided mode.
*/
function getChinaAndroidTrialProductId(mode) {
	return TRIAL_PRODUCT_IDS[mode];
}
//#endregion
//#region src/price.ts
/**
* Universal price parsing and formatting utilities for paywall apps.
*
* Handles localized price strings from native IAP systems with various formats:
* - Currency prefix: "$60.00", "€45.00", "£30.00"
* - Currency suffix: "60.00 S₣", "45,00 €"
* - Different decimal separators: "60.00" vs "60,00"
*/
/**
* Removes trailing zeros from a price string while preserving currency symbols.
*
* @example
* removeTrailingZeros("$10.50") // "$10.5"
* removeTrailingZeros("$10.00") // "$10"
* removeTrailingZeros("50.00 S₣") // "50 S₣"
* removeTrailingZeros("€45,00") // "€45"
*/
function removeTrailingZeros(price) {
	const notThousands = "(?!\\d{3}(?:\\s|[^\\d]|$))";
	return price.replace(new RegExp(`([.,])${notThousands}0+(?=\\s|[^\\d]|$)`, "g"), "").replace(new RegExp(`([.,])${notThousands}(\\d*?)0+(?=\\s|[^\\d]|$)`, "g"), "$1$2").replace(new RegExp(`([.,])${notThousands}(?=\\s|[^\\d]|$)`, "g"), "");
}
/**
* Parses a localized price string into its components.
*
* @example
* parsePriceComponents("$60.00") // { value: 60, currency: "$", isCurrencyPrefix: true, decimalSeparator: "." }
* parsePriceComponents("45,00 €") // { value: 45, currency: "€", isCurrencyPrefix: false, decimalSeparator: "," }
*/
function parsePriceComponents(priceLocalized) {
	const numericStr = priceLocalized.match(/[\d]+(?:[.,]\d+)*/g)?.[0] ?? "0";
	const lastDotIndex = numericStr.lastIndexOf(".");
	const lastCommaIndex = numericStr.lastIndexOf(",");
	let decimalSeparator;
	let value;
	if (lastDotIndex > -1 && lastCommaIndex > -1) {
		if (lastDotIndex > lastCommaIndex) {
			decimalSeparator = ".";
			value = parseFloat(numericStr.replace(/,/g, ""));
		} else {
			decimalSeparator = ",";
			value = parseFloat(numericStr.replace(/\./g, "").replace(",", "."));
		}
	} else if (lastCommaIndex > -1) {
		if (numericStr.length - lastCommaIndex - 1 === 3) {
			decimalSeparator = ".";
			value = parseFloat(numericStr.replace(/,/g, ""));
		} else {
			decimalSeparator = ",";
			const intPart = numericStr.substring(0, lastCommaIndex).replace(/,/g, "");
			const decPart = numericStr.substring(lastCommaIndex + 1);
			value = parseFloat(`${intPart}.${decPart}`);
		}
	} else if (lastDotIndex > -1) {
		if (numericStr.length - lastDotIndex - 1 === 3) {
			decimalSeparator = ",";
			value = parseFloat(numericStr.replace(/\./g, ""));
		} else {
			decimalSeparator = ".";
			value = parseFloat(numericStr);
		}
	} else {
		decimalSeparator = ".";
		value = parseFloat(numericStr);
	}
	const currency = priceLocalized.replace(/[\d.,\s]/g, "").trim();
	const isCurrencyPrefix = !priceLocalized.trim().match(/^[\d]/);
	return {
		value,
		currency,
		isCurrencyPrefix,
		decimalSeparator
	};
}
/**
* Formats a numeric value using the format from a localized price string.
* Preserves currency symbol position and decimal separator.
*
* @example
* formatPriceWithLocale(5, "$60.00") // "$5"
* formatPriceWithLocale(5.5, "$60.00") // "$5.5"
* formatPriceWithLocale(5, "60,00 €") // "5 €"
*/
function formatPriceWithLocale(value, priceLocalized, options = {}) {
	const { decimals = 2, removeTrailingZeros: shouldRemoveZeros = true } = options;
	const { currency, isCurrencyPrefix, decimalSeparator } = parsePriceComponents(priceLocalized);
	let formattedValue = value.toFixed(decimals);
	if (decimalSeparator === ",") formattedValue = formattedValue.replace(".", ",");
	let result;
	if (isCurrencyPrefix) result = `${currency}${formattedValue}`;
	else result = `${formattedValue} ${currency}`;
	if (shouldRemoveZeros) result = removeTrailingZeros(result);
	return result;
}
/**
* Calculates the monthly price from an annual subscription price.
* Preserves the original price format (currency position, decimal separator).
*
* @example
* calculateMonthlyPrice({ price_localized: "$60.00", price_value: 60 })
* // "$5"
*
* calculateMonthlyPrice({ price_localized: "120,00 €", price_value: 120 })
* // "10 €"
*/
function calculateMonthlyPrice(product) {
	return formatPriceWithLocale(product.price_value / 12, product.price_localized);
}
/**
* Replaces the numeric value in a localized price string with a new value.
* Useful for displaying calculated prices (e.g., per-month) while preserving
* the original currency format.
*
* @example
* replacePrice("$60.00", 5) // "$5"
* replacePrice("60,00 €", 5) // "5 €"
* replacePrice("S₣ 120.00", 10) // "S₣ 10"
*/
function replacePrice(priceLocalized, newValue, options = {}) {
	return formatPriceWithLocale(newValue, priceLocalized, options);
}
//#endregion
//#region src/experiment-products.ts
/**
* Fetches products for the pricing experiment from the Service API.
*
* @param serviceApi - The ky instance for the service API
* @param options - Options including userId, platform, and optional storeRegion
* @returns Array of experiment products
*/
async function fetchExperimentProducts(serviceApi, options) {
	const { userId, platform, storeRegion } = options;
	const searchParams = {
		platform,
		product_type: "subscription"
	};
	if (storeRegion) searchParams.store_region = storeRegion;
	return await serviceApi.get(`store/users/${userId}/products`, { searchParams }).json();
}
/**
* Infers product type (annual/monthly, early bird/default) from the product title.
*
* @param title - The product title from the API
* @returns Object with isAnnual and isEarlyBird flags
*/
function inferProductType(title) {
	const lowerTitle = title.toLowerCase();
	return {
		isAnnual: lowerTitle.includes("年方案") || lowerTitle.includes("annual") || lowerTitle.includes("yearly") || lowerTitle.includes("year"),
		isEarlyBird: lowerTitle.includes("早鳥") || lowerTitle.includes("early bird") || lowerTitle.includes("earlybird") || lowerTitle.includes("early_bird")
	};
}
/**
* Gets the platform-specific SKU from an experiment product.
*
* @param product - The experiment product
* @param platform - The payment platform
* @returns The appropriate SKU for the given platform
*/
function getSkuForPlatform(product, platform) {
	switch (platform) {
		case "ios": return product.iap_sku_ios;
		case "googleplay_android": return product.iap_sku_android;
		case "china_android": return product.iap_sku_android_cn;
	}
}
//#endregion
//#region src/restore.ts
var RestoreNoTransactionError = class extends Error {
	constructor() {
		super("No transactions found");
		this.name = "RestoreNoTransactionError";
	}
};
var RestoreError = class extends Error {
	status;
	constructor(message, status) {
		super(message);
		this.name = "RestoreError";
		this.status = status;
	}
};
async function restoreSubscription(options) {
	const { api, userId, platform, getAllUnclosedTransactions, getAllEntitlements, closeTransaction, currentSubscriptionExpiresAt } = options;
	let storeReadFailed = false;
	const subscriptionTransactions = (result) => {
		if (!result.success) {
			storeReadFailed = true;
			return [];
		}
		return result.data.transactions.filter((transaction) => transaction.product_type === "subscription");
	};
	const [transactions, entitlements] = await inStep("restore_find_transaction", () => Promise.all([getAllUnclosedTransactions().then(subscriptionTransactions), getAllEntitlements().then(subscriptionTransactions)]));
	let transaction;
	if (transactions.length > 0) {
		const [latestTransaction] = transactions.sort((a, b) => new Date(b.purchased_at).getTime() - new Date(a.purchased_at).getTime());
		transaction = latestTransaction;
	} else if (entitlements.length > 0) {
		const [latestEntitlement] = entitlements.sort((a, b) => new Date(b.purchased_at).getTime() - new Date(a.purchased_at).getTime());
		transaction = latestEntitlement;
	} else throw tagFailure(new RestoreNoTransactionError(), "restore_find_transaction", { cause: storeReadFailed ? "native_failed" : "none" });
	const { data: openedPurchases } = await inStep("restore_find_order", () => api.service.get(`store/users/${userId}/purchase`, { searchParams: {
		status: "open",
		page_size: 10,
		page: 1
	} }).json());
	const [openedPurchase] = openedPurchases.filter((purchase) => purchase.purchase_type === (platform === "china_android" ? "iap" : "subscription") && purchase.iap_sku === transaction.sku).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
	const purchaseId = openedPurchase?.id;
	if (openedPurchase) await inStep("restore_mark_processing", () => api.service.post(`store/users/${userId}/purchase/${openedPurchase.id}/mark-processing`), { purchaseId });
	let receiptRequestUrl = `projects/6/users/${userId}`;
	let data = {};
	if (platform === "china_android") {
		receiptRequestUrl += "/pending_iap_receipts";
		data = { receipt_data: transaction.transaction_id };
	} else {
		receiptRequestUrl += "/subscriptions";
		data = { transaction_id: transaction.transaction_id };
	}
	await inStep("restore_post_transaction", () => api.receipt.post(receiptRequestUrl, { json: {
		...data,
		sku_id: transaction.sku,
		payment_system_id: paymentSystemIdByPlatform[platform]
	} }), { purchaseId });
	try {
		await closeTransaction(transaction);
	} catch (error) {
		console.error("Failed to close restored transaction", error);
	}
	try {
		return await waitForSubscriptionStatus({
			serviceApi: api.service,
			userId,
			shouldResolve: (status) => {
				return status.service_level === "subscription" && status.subscription.expires_at !== currentSubscriptionExpiresAt;
			}
		});
	} catch {
		return null;
	}
}
/** Runs a restore step, tagging what it throws with the step (ADR 0037). */
async function inStep(step, work, details = {}) {
	try {
		return await work();
	} catch (error) {
		throw tagFailure(error, step, details);
	}
}
//#endregion
//#region src/subscribe-flow.ts
function classifyPurchaseError(error) {
	if (error instanceof PurchaseConfirmationPendingError) return {
		kind: "confirmation_pending",
		failure: error.failure
	};
	const { step, cause, purchaseId, transactionId } = readFailureTag(error, "purchase");
	const failure = {
		code: failureCode(step, cause),
		...purchaseId !== void 0 && { purchaseId }
	};
	if (error instanceof NativeStoreError) return {
		kind: "payment_timeout",
		detail: error.message,
		failure
	};
	if (error instanceof AfterPaymentError) {
		const orderTransactionId = transactionId ?? error.transactionId;
		return {
			kind: "illegal_transaction_restore",
			failure: {
				...failure,
				purchaseId: purchaseId ?? error.purchaseId,
				...orderTransactionId != null && { transactionId: orderTransactionId }
			}
		};
	}
	if (error instanceof StaleTransactionError) return {
		kind: "unknown",
		failure
	};
	if (error instanceof SubscriptionConflictError) return {
		kind: "subscription_conflict",
		ownerUid: error.ownerUid,
		failure
	};
	if (cause === "timeout" || cause === "network") return {
		kind: "no_internet",
		failure
	};
	if (error instanceof PromotionalOfferSignatureError) return {
		kind: "unknown",
		failure
	};
	const status = httpStatusOf(cause);
	if (status !== void 0) return {
		kind: "http_error",
		status,
		failure
	};
	return {
		kind: "unknown",
		failure
	};
}
function httpStatusOf(cause) {
	const match = /^http_(\d+)$/.exec(cause);
	return match ? Number(match[1]) : void 0;
}
function deriveSuccessFacts(status, product) {
	const isSubscription = status?.service_level === "subscription";
	return {
		isSubscription,
		isTrialSuccess: isSubscription && status.subscription.is_in_trial,
		expiresAt: isSubscription ? status.subscription.expires_at : void 0,
		sku: product.sku
	};
}
function createSubscribeFlow(config) {
	const { api, platform, native, telemetry, getUserId, getReceiptSystemAccountId, getDebugSimulate, getCurrentSubscriptionExpiresAt, shouldResolve: shouldResolveForSku, fetchServiceLevelStatus } = config;
	const run = async (product, options) => {
		const userId = await getUserId().catch((error) => {
			throw tagFailure(error, "auth");
		});
		const receiptSystemAccountId = getReceiptSystemAccountId();
		native.logEvent({
			event_name: "receipt_system_account_id_status",
			event_params: { is_present: String(!!receiptSystemAccountId) }
		});
		const shouldResolve = shouldResolveForSku ? bindPurchasedSku(shouldResolveForSku, product.sku) : getCurrentSubscriptionExpiresAt ? buildShouldResolve(getCurrentSubscriptionExpiresAt()) : void 0;
		const onStepError = telemetry ? buildStepTelemetry(telemetry, platform, product.sku) : void 0;
		return subscribe({
			api,
			platform,
			userId,
			product,
			nativePurchase: native.purchase,
			closeTransaction: native.closeTransaction,
			getAllUnclosedTransactions: native.getAllUnclosedTransactions,
			receiptSystemAccountId,
			offer: options?.offer,
			shouldResolve,
			fetchServiceLevelStatus,
			readServiceLevel: native.refreshServiceLevel,
			debugSimulateAfterPaymentError: getDebugSimulate?.(),
			logEvent: native.logEvent,
			onStepError
		});
	};
	const handleSuccess = (status, product) => {
		native.logEvent({
			event_name: "product_sku_purchased_success",
			event_params: { sku_id: product.sku }
		});
		const facts = deriveSuccessFacts(status, product);
		native.logEvent({
			event_name: facts.isTrialSuccess ? "plus_trial_success" : "plus_subscribe_success",
			event_params: { sku_id: product.sku }
		});
		native.refreshServiceLevel();
		return facts;
	};
	const restore = async () => {
		const userId = await getUserId().catch((error) => {
			throw tagFailure(error, "restore_auth");
		});
		return restoreSubscription({
			api,
			userId,
			platform,
			getAllUnclosedTransactions: native.getAllUnclosedTransactions,
			getAllEntitlements: native.getAllEntitlements,
			closeTransaction: native.closeTransaction,
			currentSubscriptionExpiresAt: getCurrentSubscriptionExpiresAt?.()
		});
	};
	return {
		run,
		handleSuccess,
		restore
	};
}
function bindPurchasedSku(shouldResolve, purchasedSku) {
	return (status) => shouldResolve(status, purchasedSku);
}
function buildShouldResolve(currentSubscriptionExpiresAt) {
	return (status) => status.service_level === "subscription" && status.subscription.expires_at !== currentSubscriptionExpiresAt;
}
function buildStepTelemetry(telemetry, platform, productSku) {
	return (error, context) => {
		if (context.step === "native_purchase" && error instanceof NativeStoreError && error.message === "interrupted") return;
		const failure = readFailureTag(error, "purchase");
		const failureCode = failure.step === "purchase" ? `${context.step}:${failure.cause}` : `${failure.step}:${failure.cause}`;
		if (error instanceof Error) error.name = "SubscriptionError";
		telemetry.captureException(error, {
			step: context.step,
			failure_code: failureCode,
			purchase_id: context.purchaseId,
			transaction_id: context.transactionId,
			platform,
			product_sku: productSku
		});
	};
}
//#endregion
export { AbortError, AfterPaymentError, DEFAULT_INFRA_BASE_URLS, DEFAULT_RECEIPT_BASE_URLS, DEFAULT_SERVICE_BASE_URLS, FailureCauseError, NativeStoreError, PaymentSystemID, PromotionalOfferSignatureError, PurchaseConfirmationPendingError, RestoreError, RestoreNoTransactionError, SERVICE_LEVEL_POLL_TIMEOUT_MS, StaleTransactionError, SubscriptionConflictError, calculateMonthlyPrice, classifyPurchaseError, createBooleanStore, createPaywallApi, createProductsStore, createSubscribeFlow, createSubscriptionPacks, createUserInfoSource, deriveSuccessFacts, ensureAuthenticatedUser, failureCause, failureCode, failureOf, fetchExperimentProducts, formatPriceWithLocale, getChinaAndroidTrialProductId, getSkuForPlatform, inferProductType, isConnectionFailure, mapPlatformToServiceApi, nativeErrorCause, parsePriceComponents, paymentSystemIdByPlatform, readFailureTag, readOwnerUid, removeTrailingZeros, replacePrice, restoreSubscription, subscribe, tagFailure, waitForSubscriptionStatus, withFailure };

//# sourceMappingURL=index.js.map